import { plural } from "@lingui/core/macro";
import { useEffect, useMemo, useState } from "react";
import {
    add,
    addDays,
    addHours,
    eachDayOfInterval,
    endOfMonth,
    endOfWeek,
    format,
    getDay,
    isAfter,
    isBefore,
    isEqual,
    isSameMonth,
    isThisMonth,
    isToday,
    parse,
    startOfDay,
    startOfToday,
    startOfWeek,
    startOfYesterday,
} from "date-fns";
import { fr,enUS,he,de,it,pt,ru,es,el,ro } from "date-fns/locale";
import { useLingui } from "@lingui/react";
const calendarLocales={fr,en:enUS,he,de,it,pt,ru,es,gr:el,ro};
import { CheckCircle2, ChevronLeft, ChevronRight } from "lucide-react";
import TimesBar from "./components/timesBar";
import { dayNames } from "./lib/utils";

// Fake data
const reservations = [
    addHours(startOfToday(), 5).toString(),
    addHours(startOfToday(), 6).toString(),
    addHours(startOfToday(), 7).toString(),
    addHours(startOfToday(), 8).toString(),
    addHours(startOfToday(), 9).toString(),
    addDays(new Date(addHours(startOfToday(), 4)), 3).toString(),
    addDays(new Date(addHours(startOfToday(), 4)), 8).toString(),
];

// --- Composant Header ---
function CalendarHeader({ currentMonth, prevMonth, nextMonth, maxPreviousDate }) {
    const {i18n}=useLingui();
    const locale=calendarLocales[i18n.locale]||fr;
    return (
        <div style={{ display: "flex", alignItems: "center",justifyContent:"space-between" }}>
            <button
                type="button"
                onClick={prevMonth}
                disabled={maxPreviousDate && isThisMonth(new Date(currentMonth))}
                style={{ all: "unset",cursor:"pointer", display: "flex", justifyContent: "flex-start" }}
            >
                <ChevronLeft
                    size={20}
                    aria-hidden="true"
                    style={{
                        color: maxPreviousDate && isThisMonth(new Date(currentMonth)) ? "#d1d5db" : "inherit",
                    }}
                />
            </button>
            <h2
                style={{
                    fontSize: 15,
                    fontWeight: 600,
                    color: "#431407",
                    display: "flex",
                    justifyContent: "center",
                }}
            >
                {format(parse(currentMonth, "MMM-yyyy", new Date()), "MMMM yyyy", { locale })}
            </h2>
            <button type="button" onClick={nextMonth} style={{ all: "unset",cursor:"pointer", display: "flex", justifyContent: "flex-end" }}>
                <ChevronRight size={20} aria-hidden="true" />
            </button>
        </div>
    );
}

// --- Composant Noms des jours ---
function CalendarDayNames() {
    const {i18n}=useLingui();
    return (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", marginTop: "8px" }}>
            {dayNames.map((day, i) => {
                day = new Intl.DateTimeFormat(i18n.locale === "gr" ? "el" : i18n.locale,{weekday:"short"}).format(new Date(2026,9,5+i));
                const isWeekend = i >= 5;
                return (
                    <div
                        key={i}
                        style={{
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            fontSize: "14px",
                            color: isWeekend ? "#fb923c" : "#3b82f6",
                            backgroundColor: isWeekend ? "#fff7ed" : "transparent",
                            borderTopLeftRadius: isWeekend ? "8px" : "0",
                            borderTopRightRadius: isWeekend ? "8px" : "0",
                            padding: "8px 0",
                        }}
                    >
                        {day}
                    </div>
                );
            })}
        </div>
    );
}

// --- Composant Case d’un jour ---
function CalendarDayButton({
                               day,
                               dayIdx,
                               selectedDay,
                               today,
                               maxPreviousDate,
                               availableTimes,
                               availableTimesForDay,
                               onSelect,
                               firstDayCurrentMonth,
                           }) {
    const [hover, setHover] = useState(false);

    const isSelected = isEqual(day, selectedDay);
    const isCurrent = isToday(day);
    const isPast = maxPreviousDate && isBefore(day, maxPreviousDate);

    const baseStyle = {
        border: "none",
        width: "48px",
        height: "48px",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        padding: "8px",
        borderRadius: "12px",
        backgroundColor: "#f9fafb",
        position: "relative",
        cursor: isPast ? "not-allowed" : availableTimes ? "pointer" : "default",
        fontSize: hover && availableTimes ? "1.125rem" : "1rem",
        fontWeight: isSelected || isCurrent ? "600" : "400",
        color: "#111827",
    };

    if (isSelected) {
        baseStyle.backgroundColor = "#ffedd5"; // orange-100
        baseStyle.color = "#0f172a";
    }

    if (isSelected && isCurrent) {
        baseStyle.backgroundColor = "#bfdbfe"; // blue-200
    }

    if (isCurrent && !isSelected) {
        baseStyle.backgroundColor = "#eff6ff"; // blue-50
        baseStyle.color = "#1e3a8a";
    }

    if (!isSameMonth(day, firstDayCurrentMonth) && !isSelected) {
        baseStyle.color = "#9ca3af"; // gray-400
    }

    if (isPast) {
        baseStyle.color = "#991b1b"; // red-800
        baseStyle.backgroundColor = "#fef2f2"; // red-50
    }

    return (
        <div
            key={day.toString()}
            style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "56px",
            }}
        >
            <button
                type="button"
                style={baseStyle}
                onClick={() => !isPast && availableTimes && onSelect(day)}
                onMouseEnter={() => setHover(true)}
                onMouseLeave={() => setHover(false)}
                disabled={isPast}
            >
                {isAfter(day, startOfYesterday()) && availableTimes > 0 && hover && (
                    <span
                        style={{
                            position: "absolute",
                            top: 0,
                            transform: "translate(-2px, -16px)",
                            zIndex: 10,
                            fontSize: "11px",
                            backgroundColor: "#0f172a",
                            color: "#f1f5f9",
                            padding: "2px 4px",
                            borderRadius: "6px",
                            display: "flex",
                            gap: "4px",
                        }}
                    >
                        <span>{plural(availableTimes, {one: "# créneau disponible", other: "# créneaux disponibles"})}</span>
                    </span>
                )}

                <time dateTime={format(day, "yyyy-MM-dd")}>{format(day, "d")}</time>

                {isSelected && (
                    <CheckCircle2
                        style={{
                            position: "absolute",
                            top: 0,
                            right: 0,
                            height: "18px",
                            width: "18px",
                            transform: "translate(4px, -4px)",
                            color: "#7c2d12",
                        }}
                    />
                )}

                <TimesBar times={availableTimesForDay} />
            </button>
        </div>
    );
}

// --- Composant Principal ---
export default function CalendarFormRender({ value, onChange, onChangePanel, slots, unselectable }) {
    const [calendarTouched, setCalendarTouched] = useState(false);

    let today = startOfToday();
    let maxPreviousDate = unselectable ? null : today;

    let [currentMonth, setCurrentMonth] = useState(format(today, "MMM-yyyy"));
    let [selectedDay, setSelectedDay] = useState(value || undefined);
    let firstDayCurrentMonth = parse(currentMonth, "MMM-yyyy", new Date());

    let days = useMemo(
        () =>
            eachDayOfInterval({
                start: startOfWeek(firstDayCurrentMonth, { weekStartsOn: 1 }),
                end: endOfWeek(endOfMonth(firstDayCurrentMonth), { weekStartsOn: 1 }),
            }),
        [firstDayCurrentMonth]
    );

    useEffect(() => {
        if (selectedDay !== value && onChange) {
            onChange(selectedDay);
        }
    }, [selectedDay]);

    useEffect(() => {
        if (onChangePanel) {
            onChangePanel(parse(currentMonth, "MMM-yyyy", new Date()));
        }
    }, [currentMonth]);

    const [availableTimesInThisMonth, setAvailableTimesInThisMonth] = useState([]);
    const [availableTimesInThisMonthForEachDay, setAvailableTimesInThisMonthForEachDay] = useState([]);

    function prevMonth() {
        let firstDayNextMonth = add(firstDayCurrentMonth, { months: -1 });
        setCurrentMonth(format(firstDayNextMonth, "MMM-yyyy"));
    }

    function nextMonth() {
        let firstDayNextMonth = add(firstDayCurrentMonth, { months: 1 });
        setCurrentMonth(format(firstDayNextMonth, "MMM-yyyy"));
    }

    useMemo(() => {
        let thisMonthTimesLength = [];
        let thisMonthTimesEachDay = [];
        days.map((day) => {
            const _startString = startOfDay(day).toLocaleDateString("fr-FR", { timeZone: "Europe/Paris" });
            let freeTimes = slots.filter(
                (s) => _startString === new Date(s).toLocaleDateString("fr-FR", { timeZone: "Europe/Paris" })
            );
            thisMonthTimesLength.push(freeTimes.length);
            thisMonthTimesEachDay.push(freeTimes);
        });

        setAvailableTimesInThisMonth(thisMonthTimesLength);
        setAvailableTimesInThisMonthForEachDay(thisMonthTimesEachDay);
    }, [currentMonth, slots]);

    return (
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "8px" }}>
            <div style={{ display: "flex", flexDirection: "column", gap: "8px", height: "400px", width: "380px" }}>
                <CalendarHeader
                    currentMonth={currentMonth}
                    prevMonth={prevMonth}
                    nextMonth={nextMonth}
                    maxPreviousDate={maxPreviousDate}
                />

                <CalendarDayNames />

                <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", fontSize: "14px" }}>
                    {days.map((day, idx) => (
                        <CalendarDayButton
                            key={day.toString()}
                            day={day}
                            dayIdx={idx}
                            selectedDay={selectedDay}
                            today={today}
                            maxPreviousDate={maxPreviousDate}
                            availableTimes={availableTimesInThisMonth[idx]}
                            availableTimesForDay={availableTimesInThisMonthForEachDay[idx]}
                            onSelect={(d) => {
                                setCalendarTouched(true);
                                if (unselectable && selectedDay && selectedDay.getTime() === d.getTime()) {
                                    setSelectedDay(null);
                                } else {
                                    setSelectedDay(d);
                                }
                            }}
                            firstDayCurrentMonth={firstDayCurrentMonth}
                        />
                    ))}
                </div>
            </div>
        </div>
    );
}
